/**
 * Chart settings: the defaults and the check of what the backend sends. They are
 * saved on the user, only the drawing toolbar position and the chart height
 * stay in this browser: they depend on this screen.
 */

import {
  CANDLE_DEFAULT_UP_COLOR,
  CANDLE_DEFAULT_DOWN_COLOR,
  SHADOW_STYLE,
  SHADOW_STYLE_DEFAULT,
} from "../constants";
import { CHART_RESOLUTIONS } from "./effectiveChartSettings";
import { parseFavoriteIndicators, parseSavedIndicators } from "./savedIndicators";

/** Default settings when nothing is saved yet. */
export const DEFAULT_SETTINGS = {
  /** CHART_TYPE_* value: 1 = Candles (default) */
  chartType: 1,
  /** Resolution string, e.g. "1", "60", "D" */
  resolution: "1",
  /** Show the bar countdown timer */
  timerEnabled: true,
  /** Autoscroll to latest bar when new bar appears and user is near right edge */
  autoscrollEnabled: true,
  /** Grid snap of drawings to OHLC (E14, TV-18) */
  gridSnapEnabled: false,
  /** Up (bullish) candle/bar/HA color */
  candleUpColor: CANDLE_DEFAULT_UP_COLOR,
  /** Down (bearish) candle/bar/HA color */
  candleDownColor: CANDLE_DEFAULT_DOWN_COLOR,
  /** Wick width of Candles and Heikin Ashi: SHADOW_STYLE value (E18) */
  shadowStyle: SHADOW_STYLE_DEFAULT,
  /** Indicators of the chart in the order they were added: { id, inputs, styles, isVisible } (TV-14) */
  indicators: [],
  /** Registry ids of the indicators starred in the indicators menu (TV-14) */
  favoriteIndicators: [],
  /** Lines of the open positions on the chart */
  plLinesVisible: true,
};

const SHADOW_STYLES = Object.values(SHADOW_STYLE);

const parseToolbarPosition = (position) =>
  position && Number.isFinite(position.x) && Number.isFinite(position.y)
    ? { x: position.x, y: position.y }
    : null;

const DRAWING_TOOLBAR_POSITION_KEY = "binify.chart.drawing_toolbar_position";

/**
 * @returns {{ x: number, y: number }|null} where the drawing toolbar was dragged
 *   to, CSS px of the chart; null: not yet (TV-19)
 */
export const loadDrawingToolbarPosition = () => {
  try {
    return parseToolbarPosition(
      JSON.parse(localStorage.getItem(DRAWING_TOOLBAR_POSITION_KEY)),
    );
  } catch (_e) {
    return null;
  }
};

/** @param {{ x: number, y: number }|null} position */
export const saveDrawingToolbarPosition = (position) => {
  try {
    localStorage.setItem(DRAWING_TOOLBAR_POSITION_KEY, JSON.stringify(position));
  } catch (_e) {
    // Ignore write errors (e.g. private mode storage quota)
  }
};

const CHART_HEIGHT_FACTOR_KEY = "binify.chart.height_factor";

/** @returns {number|null} the chart height factor saved in this browser; null: not yet */
export const loadChartHeightFactor = () => {
  try {
    const factor = JSON.parse(localStorage.getItem(CHART_HEIGHT_FACTOR_KEY));
    return Number.isFinite(factor) ? factor : null;
  } catch (_e) {
    return null;
  }
};

/** @param {number} factor */
export const saveChartHeightFactor = (factor) => {
  try {
    localStorage.setItem(CHART_HEIGHT_FACTOR_KEY, JSON.stringify(factor));
  } catch (_e) {
    // Ignore write errors (e.g. private mode storage quota)
  }
};

/**
 * Settings from the backend: a missing or broken value falls back to the default.
 *
 * @param {object|null} saved
 * @returns {object} all the chart settings
 */
export const parseChartSettings = (saved) => {
  const parsed = saved && typeof saved === "object" ? saved : {};

  return {
    chartType:
      typeof parsed.chartType === "number"
        ? parsed.chartType
        : DEFAULT_SETTINGS.chartType,
    // A time frame no longer offered (e.g. the month) falls back to the default
    resolution: CHART_RESOLUTIONS.includes(parsed.resolution)
      ? parsed.resolution
      : DEFAULT_SETTINGS.resolution,
    timerEnabled:
      typeof parsed.timerEnabled === "boolean"
        ? parsed.timerEnabled
        : DEFAULT_SETTINGS.timerEnabled,
    autoscrollEnabled:
      typeof parsed.autoscrollEnabled === "boolean"
        ? parsed.autoscrollEnabled
        : DEFAULT_SETTINGS.autoscrollEnabled,
    gridSnapEnabled:
      typeof parsed.gridSnapEnabled === "boolean"
        ? parsed.gridSnapEnabled
        : DEFAULT_SETTINGS.gridSnapEnabled,
    candleUpColor:
      typeof parsed.candleUpColor === "string" && parsed.candleUpColor
        ? parsed.candleUpColor
        : DEFAULT_SETTINGS.candleUpColor,
    candleDownColor:
      typeof parsed.candleDownColor === "string" && parsed.candleDownColor
        ? parsed.candleDownColor
        : DEFAULT_SETTINGS.candleDownColor,
    shadowStyle: SHADOW_STYLES.includes(parsed.shadowStyle)
      ? parsed.shadowStyle
      : DEFAULT_SETTINGS.shadowStyle,
    plLinesVisible:
      typeof parsed.plLinesVisible === "boolean"
        ? parsed.plLinesVisible
        : DEFAULT_SETTINGS.plLinesVisible,
    indicators: parseSavedIndicators(parsed.indicators),
    favoriteIndicators: parseFavoriteIndicators(parsed.favoriteIndicators),
  };
};
